import type { MouseEvent } from "react";
import { NodeViewWrapper, NodeViewContent, NodeViewProps } from "@tiptap/react";
import { CaptionsIcon, CaptionsOffIcon } from "lucide-react";

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";

export const getImageDimensions = (file: File) => {
  let width = 0;
  let height = 0;
  const reader = new FileReader();

  reader.readAsDataURL(file);
  reader.onload = function (e) {
    const image = new Image();

    image.src = e.target?.result as string;

    image.onload = function () {
      // @ts-ignore
      height = this.height;
      // @ts-ignore
      width = this.width;
    };
  };

  return { width, height };
};

export const FigureComponent = ({ editor, node, getPos }: NodeViewProps) => {
  const pos = getPos();

  return (
    <NodeViewWrapper
      as="figure"
      onClick={(e: MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        e.preventDefault();

        if (
          (e.target as HTMLElement).nodeName !== "DIV" &&
          (e.target as HTMLElement).nodeName !== "FIGCAPTION"
        ) {
          editor.chain().setNodeSelection(pos).run();
        }
      }}
      data-drag-handle
      className="relative my-4 py-0.5 indent-0 peer hover:ring-2 hover:ring-gray-200"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            contentEditable={false}
            size="icon"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();

              editor.chain().setNodeSelection(pos).run();

              if (editor.can().figureToImage()) {
                editor.chain().figureToImage().run();
              }

              editor.chain().setTextSelection(pos).run();
            }}
            className="absolute top-2 right-2 opacity-70 peer-hover:opacity-100 hover:opacity-100 size-7 rounded"
          >
            {node.content.size === 0 ? <CaptionsIcon /> : <CaptionsOffIcon />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {node.content.size === 0 ? "Add caption" : "Remove caption"}
        </TooltipContent>
      </Tooltip>
      <img
        src={node.attrs.src}
        alt={node.attrs.alt}
        title={node.attrs.title}
        contentEditable={false}
        className="block w-full h-auto cursor-default"
      />
      <NodeViewContent
        as="figcaption"
        className="leading-8 min-h-[32px] w-auto mt-0.5 mr-0.5 ml-0.5 px-2 font-sans text-sm before:absolute before:content-[attr(data-placeholder)] before:opacity-70"
        data-placeholder={node.content.size === 0 ? "Your caption" : undefined}
      />
    </NodeViewWrapper>
  );
};

export const FigureNoCaption = ({
  editor,
  node,
  getPos,
  updateAttributes,
}: NodeViewProps) => {
  const pos = getPos();

  return (
    <NodeViewWrapper
      as="div"
      data-drag-handle
      className="block relative my-4 py-0.5 indent-0 rounded-sm peer hover:ring-2 hover:ring-gray-200"
    >
      <Button
        contentEditable={false}
        size="xs"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();

          editor.chain().setNodeSelection(pos).run();

          if (editor.can().imageToFigure()) {
            editor.chain().imageToFigure().run();
          }
        }}
        className="absolute top-2 right-2 opacity-70 peer-hover:opacity-100 font-sans hover:opacity-100"
      >
        Add Caption
      </Button>
      <img
        src={node.attrs.src}
        alt={node.attrs.alt}
        title={node.attrs.title}
        contentEditable={false}
        onLoad={(e) => {
          const { naturalWidth, naturalHeight } = e.target as HTMLImageElement;

          updateAttributes({ width: naturalWidth, height: naturalHeight });
        }}
        className="block w-full h-auto cursor-default"
      />
    </NodeViewWrapper>
  );
};
